import { RefreshCw } from 'lucide-react'

import { useCheckStudioSession, type StudioSessionOut } from '@/api/endpoints/engagement'
import { Button } from '@/components/ui/Button'
import { Callout } from '@/components/ui/Callout'
import { Spinner } from '@/components/ui/Spinner'
import { errorMessage } from '@/lib/errors'
import styles from './ChannelEditDialog.module.scss'

/** Onde está o passo a passo do login manual do perfil do Studio. */
const RELOGIN_URL =
  'https://github.com/LucasCalazans/prism/blob/master/apps/backend/docs/studio-thumbnails.md#provisionar-ou-refazer-o-login'

/** Aviso fixo no topo de Posts e Interações quando a sessão do Studio expirou. */
export function SessionNotice({ session }: { session: StudioSessionOut | undefined }) {
  const check = useCheckStudioSession()
  if (session?.state !== 'expired') return null
  const result = check.data
  const stillExpired = result?.state === 'expired' && !result.busy
  return (
    <div className={styles.stickyNotice}>
      <Callout
        title="A sessão do YouTube Studio expirou"
        action={
          <Button
            variant="secondary"
            size="sm"
            leadingIcon={check.isPending ? <Spinner /> : <RefreshCw />}
            aria-disabled={check.isPending}
            onClick={() => {
              if (!check.isPending) check.mutate()
            }}
          >
            {check.isPending ? 'Verificando…' : 'Verificar sessão'}
          </Button>
        }
      >
        <p>Posts e corações ficam na fila. Post com horário vencido é reprogramado; comentário fora da janela é ignorado.</p>
        <p>
          <a href={RELOGIN_URL} target="_blank" rel="noreferrer">
            Como refazer o login
          </a>
        </p>
        {result?.busy ? <p>O Studio está em uso por outra tarefa agora. Tente de novo em alguns minutos.</p> : null}
        {stillExpired ? <p>A sessão continua expirada.</p> : null}
        {check.isError ? <p>{errorMessage(check.error, 'Não foi possível verificar agora.')}</p> : null}
      </Callout>
    </div>
  )
}

/** Este servidor não tem a automação ligada (o desktop não tem o perfil do Studio). */
export function AutomationNotice({ kind }: { kind: 'engagement' | 'posts' }) {
  return (
    <Callout variant="neutral" title="A automação do Studio está desligada neste servidor">
      {kind === 'posts' ? (
        <p>
          Nenhum post é gerado nem programado até <code>STUDIO_POSTS_ENABLED</code> ser ligado. A configuração fica
          salva e a prévia funciona.
        </p>
      ) : (
        <p>
          Com <code>STUDIO_ENGAGEMENT_ENABLED</code> desligado, nada é feito no Studio. A configuração fica salva e passa
          a valer quando a automação for ligada.
        </p>
      )}
    </Callout>
  )
}
